import { tradeRecords } from '@/mocks/tradingData';
import { accountCurve, STARTING_BALANCE } from '@/utils/accountCurve';

// Report facts the price-only ledger cannot express.
// XAUUSD contract size: 1.00 lot = 100 oz, so a 1.00 price move = $100.
const CONTRACT_SIZE = 100;
// Account leverage — used only to estimate the peak used margin (deposit load).
const LEVERAGE = 100;
// Time in market is not encoded in entry/exit prices, so it stays a report fact.
const AVG_HOLD_HOURS = 4.48; // 4h 29m
const AVG_HOLD_LABEL = '4h 29m';

const round2 = (n: number): number => Number(n.toFixed(2));
const round1 = (n: number): number => Number(n.toFixed(1));

// A structured detail block a gauge can surface on hover.
export type ReportTone = 'default' | 'profit' | 'loss';

export interface ReportDetailRow {
  label: string;
  value: string;
  tone: ReportTone;
}

export interface ReportDetail {
  title: string;
  rows: ReportDetailRow[];
  note: string;
}

const formatDay = (value: string | number): string =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const groupMoney = (n: number): string =>
  Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const signedMoney = (n: number): string => `${n < 0 ? '-' : '+'}${groupMoney(n)}`;

// Everything the MetaTrader-style "Summary" panel prints, recomputed straight
// from the single trade ledger so it can never disagree with the rest of the site.
const deriveReport = () => {
  const profits = tradeRecords.map((t) => t.profit);

  const grossProfit = round2(profits.filter((p) => p > 0).reduce((s, p) => s + p, 0));
  const grossLoss = round2(profits.filter((p) => p < 0).reduce((s, p) => s + p, 0));
  const total = round2(grossProfit + grossLoss);

  const profitFactor = grossLoss !== 0 ? round2(grossProfit / Math.abs(grossLoss)) : 0;

  const winners = tradeRecords.filter((t) => t.profit > 0);
  const losers = tradeRecords.filter((t) => t.profit < 0);
  const bestTrade = tradeRecords.reduce((a, b) => (b.profit > a.profit ? b : a));
  const worstTrade = tradeRecords.reduce((a, b) => (b.profit < a.profit ? b : a));

  // Peak-to-trough decline of the running balance, in currency and percent.
  const balances = accountCurve.map((point) => point.balance);
  let peak = balances[0];
  let maxDrawdownValue = 0;
  let maxDrawdownPercent = 0;
  balances.forEach((balance) => {
    if (balance > peak) peak = balance;
    const drop = peak - balance;
    if (drop > maxDrawdownValue) maxDrawdownValue = drop;
    const dropPercent = peak > 0 ? (drop / peak) * 100 : 0;
    if (dropPercent > maxDrawdownPercent) maxDrawdownPercent = dropPercent;
  });

  // Pin the exact peak and trough behind the headline drawdown, so the report
  // can point at the day and the specific trade where it happened.
  let percentPeak = balances[0];
  let percentPeakIndex = 0;
  let drawdownValue = 0;
  let drawdownPercent = 0;
  let drawdownPeakIndex = 0;
  let drawdownTroughIndex = 0;
  balances.forEach((balance, index) => {
    if (balance > percentPeak) {
      percentPeak = balance;
      percentPeakIndex = index;
    }
    const drop = percentPeak - balance;
    const dropPercent = percentPeak > 0 ? (drop / percentPeak) * 100 : 0;
    if (dropPercent > drawdownPercent) {
      drawdownPercent = dropPercent;
      drawdownValue = drop;
      drawdownPeakIndex = percentPeakIndex;
      drawdownTroughIndex = index;
    }
  });

  const drawdownPeakPoint = accountCurve[drawdownPeakIndex];
  const drawdownTroughPoint = accountCurve[drawdownTroughIndex];
  // accountCurve[0] is the opening balance, so the trade at index i sits at i - 1.
  const drawdownTroughTrade =
    drawdownTroughIndex >= 1 ? tradeRecords[drawdownTroughIndex - 1] : tradeRecords[0];

  const recoveryFactor = maxDrawdownValue > 0 ? round2(total / maxDrawdownValue) : 0;

  // Peak used margin against the equity carried into each trade.
  let maxDepositLoad = 0;
  tradeRecords.forEach((trade, index) => {
    const margin = (trade.volume * CONTRACT_SIZE * trade.entry) / LEVERAGE;
    const equity = accountCurve[index].balance;
    if (equity > 0) maxDepositLoad = Math.max(maxDepositLoad, (margin / equity) * 100);
  });

  const firstTime = accountCurve[0].time;
  const lastTime = accountCurve[accountCurve.length - 1].time;
  const periodDays = Math.max((lastTime - firstTime) / 86400000, 1);
  const years = Math.max(periodDays / 365, 1 / 365);

  // Annualised Sharpe from the per-trade account returns.
  const returns = profits.map((p) => p / STARTING_BALANCE);
  const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
  const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / returns.length;
  const std = Math.sqrt(variance);
  const tradesPerYear = tradeRecords.length / years;
  const sharpeRatio = std > 0 ? round2((mean / std) * Math.sqrt(tradesPerYear)) : 0;

  const tradesPerWeek = round1(tradeRecords.length / (periodDays / 7));

  // Share of calendar days over the window that carried at least one trade.
  const activeDays = new Set(tradeRecords.map((t) => t.date)).size;
  const tradingActivity = round1((activeDays / periodDays) * 100);

  const gainPercent = round2((total / STARTING_BALANCE) * 100);
  const roundedDrawdown = round2(maxDrawdownPercent);
  const roundedLoad = round2(maxDepositLoad);

  const drawdownDetail: ReportDetail = {
    title: 'Where the drawdown happened',
    rows: [
      { label: 'Peak equity', value: `$${groupMoney(drawdownPeakPoint.balance)}`, tone: 'default' },
      {
        label: 'Trough equity',
        value: `$${groupMoney(drawdownTroughPoint.balance)}`,
        tone: 'default',
      },
      {
        label: 'Decline',
        value: `-$${groupMoney(drawdownValue)} (-${drawdownPercent.toFixed(2)}%)`,
        tone: 'loss',
      },
    ],
    note: `Peak ${formatDay(drawdownPeakPoint.time)}, trough ${formatDay(
      drawdownTroughPoint.time,
    )}. Lowest point on trade #${drawdownTroughTrade.id} (${drawdownTroughTrade.side} ${
      drawdownTroughTrade.entry
    } → ${drawdownTroughTrade.exit}).`,
  };

  const profitFactorDetail: ReportDetail = {
    title: 'How profit factor is built',
    rows: [
      {
        label: `Winning trades (${winners.length})`,
        value: `+$${groupMoney(grossProfit)}`,
        tone: 'profit',
      },
      {
        label: `Losing trades (${losers.length})`,
        value: `-$${groupMoney(Math.abs(grossLoss))}`,
        tone: 'loss',
      },
      {
        label: 'Best trade',
        value: `#${bestTrade.id} · +$${groupMoney(bestTrade.profit)}`,
        tone: 'profit',
      },
      {
        label: 'Worst trade',
        value: `#${worstTrade.id} · -$${groupMoney(Math.abs(worstTrade.profit))}`,
        tone: 'loss',
      },
    ],
    note: `Best on ${formatDay(bestTrade.date)}, worst on ${formatDay(worstTrade.date)}.`,
  };

  return {
    currency: 'USD',
    gainText: `${gainPercent >= 0 ? '+' : ''}${gainPercent.toFixed(2)}%`,
    gainPositive: gainPercent >= 0,
    tradingActivityText: `${tradingActivity.toFixed(1)}%`,

    // Money movement over the recorded window is genuinely nil.
    depositsText: '0.00 (0)',
    withdrawalsText: '0.00 (0)',
    dividendsText: '0.00',
    correctionsText: '0.00',
    creditText: '0.00',

    grossProfit,
    grossProfitText: signedMoney(grossProfit),
    grossLoss,
    grossLossText: signedMoney(grossLoss),
    total,
    totalText: signedMoney(total),
    isProfit: total >= 0,

    swapsText: '0.00',
    commissionsText: '0.00',
    dividendsCalloutText: '0.00',

    sharpeRatio,
    sharpeRatioText: sharpeRatio.toFixed(2),
    profitFactor,
    profitFactorText: profitFactor.toFixed(2),
    recoveryFactor,
    recoveryFactorText: recoveryFactor.toFixed(2),
    maxDrawdownPercent: roundedDrawdown,
    maxDrawdownText: `${roundedDrawdown.toFixed(2)}%`,
    maxDepositLoad: roundedLoad,
    maxDepositLoadText: `${roundedLoad.toFixed(2)}%`,
    tradesPerWeek,
    tradesPerWeekText: tradesPerWeek.toFixed(1),
    avgHoldHours: AVG_HOLD_HOURS,
    avgHoldText: AVG_HOLD_LABEL,

    drawdownDetail,
    profitFactorDetail,
  };
};

export const strategyReport = deriveReport();